/**
 * Safe ResizeObserver helpers — avoid "ResizeObserver loop completed with
 * undelivered notifications" when observers trigger React setState mid-layout.
 */

export function isBenignResizeObserverError(message) {
    const msg = String(message || '');
    return /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i.test(msg);
}

/**
 * Observe an element and call onSize({ width, height }) on the next animation frame.
 * Skips duplicate sizes to avoid feedback loops with React Flow / charts.
 */
export function observeElementSize(el, onSize, { box = 'border-box' } = {}) {
    if (!el || typeof ResizeObserver === 'undefined') {
        if (el && typeof onSize === 'function') {
            const width = Math.round(el.clientWidth || 0);
            const height = Math.round(el.clientHeight || 0);
            onSize({ width, height });
        }
        return () => {};
    }

    let frame = 0;
    let lastW = -1;
    let lastH = -1;

    const deliver = (width, height) => {
        const w = Math.round(width || 0);
        const h = Math.round(height || 0);
        if (w === lastW && h === lastH) return;
        lastW = w;
        lastH = h;
        onSize?.({ width: w, height: h });
    };

    const schedule = (width, height) => {
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
            frame = 0;
            deliver(width, height);
        });
    };

    const ro = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const rect = entry.contentRect || {};
        schedule(rect.width ?? el.clientWidth, rect.height ?? el.clientHeight);
    });

    try {
        ro.observe(el, { box });
    } catch {
        ro.observe(el);
    }

    // Initial measurement (also deferred)
    schedule(el.clientWidth, el.clientHeight);

    return () => {
        if (frame) cancelAnimationFrame(frame);
        try { ro.disconnect(); } catch { /* ignore */ }
    };
}
